// İSG uzmanı portalı — Quiz (test) editörü (§16 authoring). Dinamik soru listesi (RHF
// `useFieldArray`); her soru: metin + **2-5 seçenek** (ekle/sil; boş seçenek reddedilir) +
// **doğru cevap** (radio → `correctOptionIndex`). En az 1 soru zorunlu.
//
// **⛔ correctOptionIndex — yalnız burada.** Doğru cevap seçimi (radio) YALNIZ bu authoring
// editöründe görünür/düzenlenir (İSG uzmanı). Personel/mobil bağlamı (Faz B / parça 049) ASLA
// görmez — cevap anahtarı başka hiçbir yüzeye taşınmaz.

import { Button, Field, Input } from '@isgportal/shared';
import { CircleDot, Plus, Trash2 } from 'lucide-react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  MAX_OPTIONS,
  MIN_OPTIONS,
  type TrainingEditorForm,
  emptyQuestion,
} from './trainingEditorForm';

export function QuizEditor() {
  const { t } = useTranslation();
  const { control, formState } = useFormContext<TrainingEditorForm>();
  const { fields, append, remove } = useFieldArray({ control, name: 'questions' });

  // Soru dizisinin kök hatası (ör. "en az 1 soru") — alan seviyesinde gösterilir.
  const questionsError =
    formState.errors.questions?.root?.message ?? formState.errors.questions?.message;

  return (
    <fieldset className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <legend className="text-sm font-semibold text-fg">
          {t('trainings.editor.quiz.title')}
        </legend>
        <Button type="button" size="sm" variant="secondary" onClick={() => append(emptyQuestion())}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('trainings.editor.quiz.addQuestion')}
        </Button>
      </div>
      <p className="-mt-2 text-xs text-muted">{t('trainings.editor.quiz.hint')}</p>

      {questionsError ? (
        <p role="alert" className="text-xs font-medium text-danger">
          {questionsError}
        </p>
      ) : null}

      {fields.length === 0 ? (
        <p className="rounded-md border border-dashed border-border-strong px-3 py-6 text-center text-sm text-muted">
          {t('trainings.editor.quiz.empty')}
        </p>
      ) : (
        <ol className="flex flex-col gap-4">
          {fields.map((field, index) => (
            <QuizQuestionCard
              key={field.id}
              index={index}
              canRemove={fields.length > 1}
              onRemove={() => remove(index)}
            />
          ))}
        </ol>
      )}
    </fieldset>
  );
}

interface QuizQuestionCardProps {
  index: number;
  canRemove: boolean;
  onRemove: () => void;
}

function QuizQuestionCard({ index, canRemove, onRemove }: QuizQuestionCardProps) {
  const { t } = useTranslation();
  const { control, register, formState, watch, setValue } = useFormContext<TrainingEditorForm>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `questions.${index}.options`,
  });

  const questionErrors = formState.errors.questions?.[index];
  const correctIndex = watch(`questions.${index}.correctOptionIndex`) ?? 0;

  function removeOption(optionIndex: number) {
    remove(optionIndex);
    // Cevap anahtarı bütünlüğü: silinen seçeneğin konumuna göre `correctOptionIndex` kaydırılır —
    // aksi hâlde radio sessizce YANLIŞ seçeneği doğru işaretler.
    if (optionIndex === correctIndex) {
      // Doğru cevabın kendisi silindi → seçimi ilk seçeneğe çek (tutarsız kalmasın).
      setValue(`questions.${index}.correctOptionIndex`, 0, { shouldValidate: true });
    } else if (optionIndex < correctIndex) {
      // Doğru cevaptan ÖNCEKİ bir seçenek silindi → indeks bir sola kayar.
      setValue(`questions.${index}.correctOptionIndex`, correctIndex - 1, { shouldValidate: true });
    }
    // optionIndex > correctIndex → doğru cevabın konumu değişmez (dokunma).
  }

  return (
    <li className="flex flex-col gap-3 rounded-lg border border-border bg-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <span className="mt-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
          {index + 1}
        </span>
        <div className="flex-1">
          <Field
            htmlFor={`question-${index}-text`}
            label={t('trainings.editor.quiz.questionText')}
            error={questionErrors?.text?.message}
            required
          >
            <Input
              id={`question-${index}-text`}
              invalid={Boolean(questionErrors?.text)}
              {...register(`questions.${index}.text`)}
            />
          </Field>
        </div>
        {canRemove ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-6"
            onClick={onRemove}
            aria-label={t('trainings.editor.quiz.removeQuestion')}
          >
            <Trash2 className="h-4 w-4 text-danger" aria-hidden="true" />
          </Button>
        ) : null}
      </div>

      <div className="flex flex-col gap-2 pl-9">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-medium text-muted">
            {t('trainings.editor.quiz.options')}
          </span>
          <span className="text-xs text-muted">{t('trainings.editor.quiz.correctHint')}</span>
        </div>

        {/* Kök seçenek hatası (2-5 aralığı) */}
        {(questionErrors?.options?.root?.message ?? questionErrors?.options?.message) ? (
          <p role="alert" className="text-xs font-medium text-danger">
            {questionErrors?.options?.root?.message ?? questionErrors?.options?.message}
          </p>
        ) : null}
        {questionErrors?.correctOptionIndex?.message ? (
          <p role="alert" className="text-xs font-medium text-danger">
            {questionErrors.correctOptionIndex.message}
          </p>
        ) : null}

        {fields.map((optionField, optionIndex) => {
          const optionError = questionErrors?.options?.[optionIndex]?.text?.message;
          return (
            <div key={optionField.id} className="flex items-start gap-2">
              <label className="mt-2.5 inline-flex cursor-pointer items-center">
                <input
                  type="radio"
                  className="h-4 w-4 accent-primary"
                  // Controlled radio: değer bağı `setValue` ile **NUMBER** yazılır. RHF
                  // `valueAsNumber` radio input'ta UYGULANMAZ (yalnız number/text) → seçim
                  // string ("0"/"1") saklanır ve Zod `z.number()` reddeder ("Doğru cevabı seçin."
                  // hatası doğru cevap seçiliyken bile). `name` grup davranışı (soru başına tek
                  // seçim); `checked` form state'ten sürülür (correctOptionIndex number kalır).
                  name={`questions.${index}.correctOptionIndex`}
                  checked={correctIndex === optionIndex}
                  onChange={() =>
                    setValue(`questions.${index}.correctOptionIndex`, optionIndex, {
                      shouldValidate: true,
                    })
                  }
                  aria-label={t('trainings.editor.quiz.markCorrect', { number: optionIndex + 1 })}
                />
              </label>
              <div className="flex-1">
                <Input
                  invalid={Boolean(optionError)}
                  placeholder={t('trainings.editor.quiz.optionPlaceholder', {
                    number: optionIndex + 1,
                  })}
                  {...register(`questions.${index}.options.${optionIndex}.text`)}
                />
                {optionError ? (
                  <p role="alert" className="mt-1 text-xs font-medium text-danger">
                    {optionError}
                  </p>
                ) : null}
              </div>
              {fields.length > MIN_OPTIONS ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-0.5"
                  onClick={() => removeOption(optionIndex)}
                  aria-label={t('trainings.editor.quiz.removeOption')}
                >
                  <Trash2 className="h-4 w-4 text-danger" aria-hidden="true" />
                </Button>
              ) : null}
            </div>
          );
        })}

        {fields.length < MAX_OPTIONS ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-fit"
            onClick={() => append({ text: '' })}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('trainings.editor.quiz.addOption')}
          </Button>
        ) : null}

        <p className="flex items-center gap-1.5 text-xs text-muted">
          <CircleDot className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
          {t('trainings.editor.quiz.correctAnswerNote', { number: (correctIndex ?? 0) + 1 })}
        </p>
      </div>
    </li>
  );
}
